import { mqMin, space, tok } from '@sunwebgroup/ui-components'
import { Button, Icon, Menu, Typography } from '@sunwebgroup/ui-components/uds'
import { useState } from 'react'
import { styled } from 'styled-components'
import { useIntl } from '@/intl'
import { useOptimizelyFlagContext } from '@/scopes/common/infrastructure/ab-testing/optimizely/useOptimizely'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useSorting } from '@/scopes/search/search/domain/hooks'
import { getDisplaySortingBy, getSortingAccordingToTestAndPartyComposition, SORT_OPTIONS } from '@/scopes/search/search/domain/sorting'
import { useSearchStore } from '@/scopes/search/search/infrastructure/store/useSearchStore'
import { SortingExplanation } from '@/scopes/search/search/pages/SearchPage/components/Results/SortingExplanation'

const Wrapper = styled.div`
  display: flex;
  align-items: center;
`

const Label = styled(Typography)`
  display: none;
  color: ${tok('semantics_content_secondary_color')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    display: flex;
  }
`

const MenuContent = styled(Menu.Content)`
  max-width: 280px;
`

const MenuTrigger = styled(Menu.Trigger)<{ $disabled: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: center;

  ${Icon} {
    margin-left: unset
  }

  ${({ $disabled }) => $disabled && `pointer-events: none;`}
`

const SortByLabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${space('primitives_space_4')};
`

function SortingHint({ by }: { by: string }) {
  switch (by) {
    case 'Popularity':
      return <SortingExplanation />
    default:
      return null
  }
}

export function Sorting() {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { isFetching, sorting, setSortingBy } = useSorting()
  const [open, setOpen] = useState(false)
  const { prioritySorting } = useOptimizelyFlagContext()
  const { filterSelection } = useSearchStore()

  function toggle() {
    setOpen(!open)
  }

  const handleChange = (value: string) => {
    const resolvedValue = getSortingAccordingToTestAndPartyComposition(
      { sorting: { by: value }, filterSelection },
      prioritySorting,
    )
    setSortingBy(resolvedValue)
    toggle()
    sendEvent({
      event: 'sortingSearch',
      description: 'sort product listing',
      element: 'dropdown',
      type: 'sorting on',
      result: resolvedValue,
      interaction: true,
    })
  }

  return (
    <Menu.Root open={open} onOpenChange={toggle}>
      <Wrapper>
        <MenuTrigger $disabled={isFetching}>
          <SortByLabelWrapper>
            <Label variant="body-m-bold-tight">{intl.formatMessage({ id: 'sort_label' })}</Label>
            <Button variant="transparentNeutral">
              {intl.formatMessage({ id: `${SORT_OPTIONS[getDisplaySortingBy(sorting.by)].translation}` })}
              <Button.Icon glyph={open ? 'ChevronUpExtraSmall' : 'ChevronDownExtraSmall'} />
            </Button>
          </SortByLabelWrapper>
        </MenuTrigger>
        <SortingHint by={getDisplaySortingBy(sorting.by)} />
      </Wrapper>
      <MenuContent position="bottom-left">
        {Object.entries(SORT_OPTIONS).map(([key, value], index) => (
          <Menu.Item
            glyph={key === getDisplaySortingBy(sorting.by) ? 'CheckMark' : undefined}
            key={key}
            supportingText={value.subtext ? intl.formatMessage({ id: value.subtext }) : undefined}
            onClick={() => handleChange(key)}
            id={`sorting_option_${index + 1}`}
          >
            {intl.formatMessage({ id: value.translation })}
          </Menu.Item>
        ))}
      </MenuContent>
    </Menu.Root>
  )
}
